import React, { memo } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EngineeringIcon from "@mui/icons-material/Engineering";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CodeIcon from "@mui/icons-material/Code";
import BugReportIcon from "@mui/icons-material/BugReport";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

export const QUALIFICATION_ROLES = [
  {
    label: "CTO",
    value: "CTO",
    Icon: EngineeringIcon,
    desc: "Chief Technology Officer",
    color: "#7C3AED",
    gradient: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
    bg: "rgba(124, 58, 237, 0.08)",
  },
  {
    label: "CEO",
    value: "CEO",
    Icon: BusinessCenterIcon,
    desc: "Chief Executive Officer",
    color: "#0284C7",
    gradient: "linear-gradient(135deg, #0284C7 0%, #38BDF8 100%)",
    bg: "rgba(2, 132, 199, 0.08)",
  },
  {
    label: "Developer",
    value: "Developer",
    Icon: CodeIcon,
    desc: "Software Engineer / Developer",
    color: "#059669",
    gradient: "linear-gradient(135deg, #059669 0%, #34D399 100%)",
    bg: "rgba(5, 150, 105, 0.08)",
  },
  {
    label: "QA Lead",
    value: "QA Lead",
    Icon: BugReportIcon,
    desc: "QA Lead / Test Engineer",
    color: "#D97706",
    gradient: "linear-gradient(135deg, #D97706 0%, #FBBF24 100%)",
    bg: "rgba(217, 119, 6, 0.08)",
  },
  {
    label: "Engineering Manager",
    value: "Engineering Manager",
    Icon: ManageAccountsIcon,
    desc: "Engineering Manager / VP Eng",
    color: "#DB2777",
    gradient: "linear-gradient(135deg, #DB2777 0%, #F472B6 100%)",
    bg: "rgba(219, 39, 119, 0.08)",
  },
  {
    label: "Other",
    value: "Other",
    Icon: PersonOutlineIcon,
    desc: "Other role",
    color: "#475569",
    gradient: "linear-gradient(135deg, #475569 0%, #94A3B8 100%)",
    bg: "rgba(71, 85, 105, 0.08)",
  },
];

const RoleCard = ({ role, onClick }) => {
  const { Icon } = role;
  return (
    <Box
      component="button"
      onClick={() => onClick(role.value)}
      aria-label={`Select role: ${role.label}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        width: "100%",
        p: "13px 14px",
        border: "1px solid rgba(255,255,255,0.55)",
        borderRadius: "12px",
        background: "rgba(255,255,255,0.52)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        cursor: "pointer",
        textAlign: "left",
        transition: "all 0.2s ease",
        outline: "none",
        fontFamily: "inherit",
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          background: role.gradient,
          opacity: 0,
          transition: "opacity 0.2s ease",
        },
        "&:hover": {
          border: `1px solid ${role.color}55`,
          background: "rgba(255,255,255,0.82)",
          boxShadow: `0 6px 20px ${role.color}22`,
          transform: "translateY(-1px)",
          "& .role-icon-box": {
            background: role.gradient,
            "& .role-icon-svg": { color: "#fff" },
          },
          "& .role-arrow": {
            opacity: 1,
            transform: "translateX(0)",
          },
          "& .role-label": { color: role.color },
        },
        "&:focus-visible": {
          border: `1.5px solid ${role.color}`,
          boxShadow: `0 0 0 3px ${role.color}28`,
        },
      }}
    >
      <Box
        className="role-icon-box"
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          background: role.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "background 0.2s ease",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Icon
          className="role-icon-svg"
          sx={{
            fontSize: "1.2rem",
            color: role.color,
            transition: "color 0.2s ease",
          }}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, position: "relative", zIndex: 1 }}>
        <Typography
          className="role-label"
          sx={{
            fontWeight: 650,
            fontSize: "0.875rem",
            color: "#1E293B",
            lineHeight: 1.3,
            fontFamily: "Segoe UI, sans-serif",
            transition: "color 0.2s ease",
            letterSpacing: "-0.01em",
          }}
        >
          {role.label}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.72rem",
            color: "#64748B",
            mt: 0.2,
            lineHeight: 1.4,
            fontFamily: "Segoe UI, sans-serif",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {role.desc}
        </Typography>
      </Box>

      <ArrowForwardIosIcon
        className="role-arrow"
        sx={{
          fontSize: "0.65rem",
          color: role.color,
          flexShrink: 0,
          position: "relative",
          zIndex: 1,
          opacity: 0,
          transform: "translateX(-4px)",
          transition: "opacity 0.2s ease, transform 0.2s ease",
        }}
      />
    </Box>
  );
};

const QualificationDialog = ({ open, onClose, onRoleSelect }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          background: "rgba(241, 245, 249, 0.78)",
          backdropFilter: "blur(28px) saturate(160%)",
          WebkitBackdropFilter: "blur(28px) saturate(160%)",
          border: "1px solid rgba(255,255,255,0.72)",
          boxShadow:
            "0 8px 32px rgba(0,0,0,0.14), 0 1px 0 rgba(255,255,255,0.9) inset",
          overflow: "hidden",
          m: { xs: 2, sm: "auto" },
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            background: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
    >
      {/* Decorative ambient glow blobs */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: -60,
          right: -40,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(151,86,216,0.18) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          bottom: -40,
          left: -30,
          width: 180,
          height: 180,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(26,158,202,0.14) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Header */}
      <Box
        sx={{
          px: { xs: 3, sm: 3.5 },
          pt: { xs: 3, sm: 3.5 },
          pb: 2.5,
          position: "relative",
          borderBottom: "1px solid rgba(255,255,255,0.5)",
        }}
      >
        {/* Top gradient line */}
        {/* <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2.5,
            background:
              "linear-gradient(90deg, #9756D8 0%, #1A9ECA 50%, #FF8052 100%)",
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          }}
        /> */}

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            {/* Eyebrow label */}
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#9756D8",
                mb: 0.75,
                fontFamily: "Segoe UI, sans-serif",
              }}
            >
              Book a Demo
            </Typography>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.1rem", sm: "1.2rem" },
                color: "#0F172A",
                fontFamily: "Segoe UI, sans-serif",
                lineHeight: 1.25,
                letterSpacing: "-0.02em",
                mb: 0.6,
              }}
            >
              What best describes your role?
            </Typography>

            <Typography
              sx={{
                fontSize: "0.825rem",
                color: "#64748B",
                fontFamily: "Segoe UI, sans-serif",
                lineHeight: 1.5,
              }}
            >
              We'll tailor the Sniffr demo specifically for you.
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close dialog"
            sx={{
              flexShrink: 0,
              mt: 0.5,
              color: "#64748B",
              background: "rgba(255,255,255,0.6)",
              border: "1px solid rgba(255,255,255,0.8)",
              backdropFilter: "blur(8px)",
              width: 30,
              height: 30,
              "&:hover": {
                background: "rgba(255,255,255,0.9)",
                color: "#0F172A",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: "0.9rem" }} />
          </IconButton>
        </Box>
      </Box>

      {/* Role grid */}
      <DialogContent
        sx={{ px: { xs: 3, sm: 3.5 }, pt: 2.5, pb: 3, position: "relative" }}
      >
        <Grid container spacing={1.25}>
          {QUALIFICATION_ROLES.map((role) => (
            <Grid item xs={12} sm={6} key={role.value}>
              <RoleCard role={role} onClick={onRoleSelect} />
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            mt: 2.5,
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #9756D8, #1A9ECA)",
              opacity: 0.5,
            }}
          />
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: "#94A3B8",
              fontFamily: "Segoe UI, sans-serif",
              letterSpacing: "0.01em",
            }}
          >
            Used only to personalize your demo — no spam.
          </Typography>
          <Box
            sx={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #1A9ECA, #FF8052)",
              opacity: 0.5,
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default memo(QualificationDialog);
